/**
 * WSH — Collection card now-playing state
 *
 * Marks the .wsh-type-card-gallery whose demo is currently playing with
 * a persistent .wsh-type-card-gallery--playing class. CSS in
 * assets/wsh-type-card.css renders the visual (oxblood top rule, play
 * button stays visible, equalizer animation).
 *
 * Data path (verified via wsh-custom-features/extensions/wsh-theme/
 * assets/wsh-audio-manager.js):
 *
 *  - The audio manager is the <wsh-audio-manager> custom element
 *    (app extension, LOCKED). It exposes:
 *      • Document events: wsh:audio:play, wsh:audio:pause,
 *        wsh:audio:track-change. Each carries detail.track with
 *        {trackUrl, recordName, productUrl, variantId, ...}.
 *      • Public getters on the element instance: .currentTrack,
 *        .isPlaying, .currentIndex.
 *  - The track object does NOT carry the Shopify product ID. The
 *    bidirectional identifier between card and track is trackUrl:
 *      • Card-side: .play__audio[data-track="..."]
 *      • Event-side: e.detail.track.trackUrl
 *
 * Re-render survival (Standing Rule 5):
 *  - filter:update swaps innerHTML on main-collection. The fresh
 *    .play__audio elements survive with the same data-track URLs
 *    (server-rendered from product.metafields.tracks.demo).
 *  - Our module-level currentTrackUrl + isPlaying are intact across
 *    the swap. On filter:update we requery the manager (defensively)
 *    and re-apply the state by trackUrl matching.
 *  - The audio manager itself owns the playback state — we only
 *    consume, never modify.
 */
(() => {
  'use strict';

  const STATE_CLASS = 'wsh-type-card-gallery--playing';
  const CARD_SEL = '.wsh-type-card-gallery';
  const AUDIO_SEL = '.play__audio[data-track]';

  /* Module-level mirror of the audio manager's current state.
     Persists across DOM re-renders. */
  let currentTrackUrl = null;
  let isPlaying = false;

  function applyState(forceAnimRestart) {
    document.querySelectorAll('.' + STATE_CLASS).forEach((c) =>
      c.classList.remove(STATE_CLASS)
    );
    if (!isPlaying || !currentTrackUrl) return;
    /* Match ALL .play__audio elements with this trackUrl. The same product
       can appear in main-collection AND in multiple Featured Collection
       carousels on the same page (e.g. a product in "Bestselling…" + the
       main grid filter results). Single-active is per PRODUCT/track, but
       every visible card instance of that product should reflect the
       playing state. Pre-fix this used querySelector (singular) and only
       the first DOM match (always the main-grid instance) was marked. */
    const audioEls = document.querySelectorAll(
      `${AUDIO_SEL}[data-track="${CSS.escape(currentTrackUrl)}"]`
    );
    audioEls.forEach((audioEl) => {
      const card = audioEl.closest(CARD_SEL);
      if (!card) return;
      card.classList.add(STATE_CLASS);

      /* Chrome quirk: CSS animations on ::before pseudo-elements can fail
         to instantiate when the parent class that generates the pseudo is
         added mid-life via JS — getAnimations() returns 0 entries even
         though getComputedStyle reports animation: wsh-card-eq running.
         This breaks the equalizer AFTER filter:update (which swaps in
         fresh DOM, then our hook adds the class to a freshly-rendered
         card). Workaround: sync toggle the modifier off + force reflow +
         on, forcing Chrome to destroy the pseudo and recreate it with a
         fresh animation instantiation.

         IMPORTANT: this toggle causes a one-frame visual flicker because
         the .--playing class adds a 3px border-top (vs the 1px hairline
         rest border) — a 2px layout shift. On regular play clicks the
         flicker reads as a double-movement bug (Phil's report). The
         toggle is ONLY needed on the filter:update path where the card's
         DOM element itself is fresh. Regular play/pause/track-change on
         long-lived DOM elements do not need it — Chrome instantiates the
         pseudo's animation cleanly when a class is added to an element
         that has existed since page load. */
      if (forceAnimRestart) {
        card.classList.remove(STATE_CLASS);
        void card.offsetHeight;
        card.classList.add(STATE_CLASS);
      }
    });
  }

  /* Track changes: a new track is being loaded — capture the URL. */
  document.addEventListener('wsh:audio:track-change', (e) => {
    const url = e?.detail?.track?.trackUrl;
    if (url) currentTrackUrl = url;
    applyState();
  });

  /* Play state: audio started/resumed. */
  document.addEventListener('wsh:audio:play', (e) => {
    const url = e?.detail?.track?.trackUrl;
    if (url) currentTrackUrl = url;
    isPlaying = true;
    applyState();
  });

  /* Pause state: audio paused (manual pause OR end-of-playlist). */
  document.addEventListener('wsh:audio:pause', () => {
    isPlaying = false;
    applyState();
    /* Reset progress line fill on all currently-playing cards. CSS keeps
       the progress bar container hidden when the .--playing class is gone,
       but we clear the fill var so a subsequent play starts at 0. */
    document.querySelectorAll('.' + STATE_CLASS).forEach((c) => {
      c.style.setProperty('--wsh-card-progress', '0');
    });
  });

  /* CL-4 (2026-10-26) · progress line.
     wsh-audio-manager dispatches wsh:audio:progress ~60Hz with
     detail.progress ∈ [0, 1]. Write that onto the currently-playing
     card as --wsh-card-progress; CSS multiplies by 100% to size the
     bottom-edge progress bar. Scoped to the already-matched .--playing
     cards so we never query the DOM for untouched cards.
     Only reads trackUrl once per event to dodge the (rare) case where
     the manager advances a playlist between dispatches. */
  document.addEventListener('wsh:audio:progress', (e) => {
    const p = e?.detail?.progress;
    if (typeof p !== 'number') return;
    const clamped = Math.max(0, Math.min(1, p));
    document.querySelectorAll('.' + STATE_CLASS).forEach((c) => {
      c.style.setProperty('--wsh-card-progress', String(clamped));
    });
  });

  /* Filter re-render: the grid swapped innerHTML, fresh card DOM is
     in place. Module-level vars are intact; defensively re-query the
     manager (in case our listeners missed an event during navigation
     or the page was loaded mid-playback) and re-apply by trackUrl. */
  document.addEventListener('filter:update', () => {
    const mgr = document.querySelector('wsh-audio-manager');
    if (mgr) {
      const t = mgr.currentTrack;
      if (t?.trackUrl) currentTrackUrl = t.trackUrl;
      isPlaying = !!mgr.isPlaying;
    }
    /* Pass forceAnimRestart=true only here — this is the path where the
       DOM was just swapped via Section Rendering API and freshly-rendered
       cards need the pseudo-animation re-instantiation. See applyState. */
    requestAnimationFrame(() => applyState(true));
  });

  /* Initial reconcile — handles SPA-like loads or back-navigation
     where the manager already has state. Defensive; on a fresh page
     load the manager is uninitialized and these getters return null/false. */
  function init() {
    const mgr = document.querySelector('wsh-audio-manager');
    if (mgr) {
      const t = mgr.currentTrack;
      if (t?.trackUrl) currentTrackUrl = t.trackUrl;
      isPlaying = !!mgr.isPlaying;
    }
    applyState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ────────────────────────────────────────────────────────────────────
     Pause-toggle intercept (Session D follow-up — fixes the
     "click playing card restarts instead of pausing" bug).

     The locked <wsh-audio-manager> click handler (in the app extension at
     wsh-custom-features/extensions/wsh-theme/assets/wsh-audio-manager.js
     line 135) always routes collection-page clicks through loadPlaylist()
     → #loadCurrentTrack() → wavesurfer.pause() + seekTo(0) + load(). That
     restarts from zero on every click, including clicks on the
     already-playing card. The same-track toggle path exists in
     loadAndPlay() (lines 31–33) but is never reached from collection-page
     clicks.

     Fix: capture-phase listener at document level. If the clicked
     .play__audio[data-track]'s URL matches the manager's currentTrack.trackUrl,
     route to the manager's existing pause()/play() (the same APIs the
     <wsh-soundbar> invokes — single state, no desync, no double-fire),
     and stopImmediatePropagation so the manager's bubble-phase handler
     does not re-fire the load-and-restart path.

     For a click on a DIFFERENT card's button, the listener falls through
     and the manager's default load-and-play runs unchanged.

     ASSUMPTION: the audio manager's own click listener is bubble-phase.
     Verified 2026-06-25 against the source at line 135 — third arg to
     addEventListener is omitted, so default capture=false (bubble). If a
     future audio-manager update moves to capture phase or adds a
     capture-phase pre-check, this intercept may not get there first.
     Re-verify on any wsh-custom-features audio update.
     ──────────────────────────────────────────────────────────────────── */
  document.addEventListener(
    'click',
    (e) => {
      const container = e.target.closest(AUDIO_SEL);
      if (!container) return;
      const mgr = document.querySelector('wsh-audio-manager');
      if (!mgr || !mgr.currentTrack) return;
      if (container.dataset.track !== mgr.currentTrack.trackUrl) return;
      // Same track currently loaded — toggle play/pause, do NOT restart.
      e.stopImmediatePropagation();
      e.preventDefault();
      if (mgr.isPlaying) mgr.pause();
      else mgr.play();
    },
    true // capture phase — runs before the audio manager's bubble listener
  );

  /* ────────────────────────────────────────────────────────────────────
     CL-4 R4 (Phil, 2026-10-26) · aria-label swap for the card play button.

     The playing-state class (applied by applyState above) swaps the icon
     via CSS, but aria-label is a DOM attribute — screen readers need it
     updated too. Mirror the state-class toggle on every change event the
     audio manager dispatches.
     ──────────────────────────────────────────────────────────────────── */
  const PLAY_BTN_SEL = '.wsh-type-card__play-btn[data-wsh-play-title]';

  function updateAriaLabels() {
    document.querySelectorAll(PLAY_BTN_SEL).forEach((btn) => {
      const title = btn.getAttribute('data-wsh-play-title') || '';
      const card = btn.closest(CARD_SEL);
      const playing = !!(card && card.classList.contains(STATE_CLASS));
      const verb = playing ? 'Pause' : 'Play';
      btn.setAttribute('aria-label', `${verb} ${title}`);
    });
  }

  // Hook into the state events: wsh:audio:play / :pause / :track-change
  // all trigger applyState() above — rerun aria sync on the same cadence.
  document.addEventListener('wsh:audio:play', updateAriaLabels);
  document.addEventListener('wsh:audio:pause', updateAriaLabels);
  document.addEventListener('wsh:audio:track-change', updateAriaLabels);
  document.addEventListener('filter:update', updateAriaLabels);
  document.addEventListener('wsh:filter:update', updateAriaLabels);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateAriaLabels);
  } else {
    updateAriaLabels();
  }

  /* ────────────────────────────────────────────────────────────────────
     R5 (Phil, 2026-10-26) · Centre Horizon slideshow-arrows on the
     picture (not the whole card) on every slideshow-component that
     contains .wsh-type-card-gallery.

     The base `slideshow-arrows` rule is `position:absolute; inset:0;
     align-items:center`, which puts controls at the FULL card center.
     For a wsh-type-card the picture is only ~70% of the card height,
     so the control center is ~91px below the picture center at 995px.

     Fix: measure the first visible .wsh-type-card__image-region per
     slideshow, set --wsh-slideshow-arrows-{top,height} on the
     slideshow-component. CSS (wsh-bundle.css) consumes those vars to
     reshape the arrows to span only the picture region, so
     align-items:center resolves to the picture center.

     Re-runs on: load, resize, filter:update, wsh:filter:update, +
     a per-slideshow ResizeObserver so that a slideshow-component
     that goes from 0 → actual width (e.g. the Horizon tab system
     revealing a hidden panel) re-aligns on first paint.
     ──────────────────────────────────────────────────────────────────── */
  const SLIDESHOW_SEL = 'slideshow-component';

  function alignSlideshowArrows() {
    if (window.innerWidth < 750) return; // phone: CSS already display:none.
    const slideshows = document.querySelectorAll(SLIDESHOW_SEL);
    for (const sc of slideshows) {
      const firstCard = sc.querySelector('.wsh-type-card-gallery');
      if (!firstCard) continue;
      const firstImage = firstCard.querySelector('.wsh-type-card__image-region');
      if (!firstImage) continue;
      const scRect = sc.getBoundingClientRect();
      const imgRect = firstImage.getBoundingClientRect();
      if (scRect.width === 0 || imgRect.width === 0) continue;
      const top = imgRect.top - scRect.top;
      const h = imgRect.height;
      sc.style.setProperty('--wsh-slideshow-arrows-top', top + 'px');
      sc.style.setProperty('--wsh-slideshow-arrows-height', h + 'px');
    }
  }

  let alignRaf = 0;
  function scheduleAlign() {
    if (alignRaf) cancelAnimationFrame(alignRaf);
    alignRaf = requestAnimationFrame(() => {
      alignRaf = 0;
      alignSlideshowArrows();
    });
  }

  function initAlign() {
    alignSlideshowArrows();
    // Delayed re-run after font + lazy image settle (same reason as
    // the pills + title formulas: layout can shift post-DCL on tier S).
    setTimeout(alignSlideshowArrows, 400);
    // ResizeObserver per slideshow-component containing a wsh-type-card.
    // Fires when the slideshow resizes from 0 (hidden tab) to actual width.
    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(scheduleAlign);
      document.querySelectorAll(SLIDESHOW_SEL).forEach((sc) => {
        if (sc.querySelector('.wsh-type-card-gallery')) ro.observe(sc);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAlign);
  } else {
    initAlign();
  }

  window.addEventListener('resize', scheduleAlign);
  document.addEventListener('filter:update', scheduleAlign);
  document.addEventListener('wsh:filter:update', scheduleAlign);
  // Font-ready: Horizon's h2 titles above a rail can shift after font swap,
  // which changes the first card's image.top. Re-align once fonts land.
  if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
    document.fonts.ready.then(scheduleAlign);
  }
})();
